<template>
  <v-hover v-slot="{ hover }">
    <v-autocomplete v-model="modelValue" :search-input.sync="searchValue" @update:search-input="debounceSearchInput"
      :items="list" :filter="i => i" :readonly="!externaledit && (IsDisabled || !edit)" ref="QPaginatedSearchDropdown"
      class="q-input" :class="[BorderClass, CompactClass, BlurrClass, InputColorClass]" :outlined="outlined"
      :background-color="BackgroundColor" :label="labelText" hide-details="auto" :disabled="IsDisabled"
      :single-line="singleline" :color="color" :item-text="textkey" :item-value="valuekey" :return-object="returnobject"
      :multiple="multiple" :small-chips="multiple" :clearable="!!externaledit && !IsEmpty && clearable" flat
      :dense="dense" :hoverbool="setHoverBool(hover)" :autofocus="autofocus"
      :placeholder="disabled ? $t('servicesystem.common_objects.commonterms.not_selectable') : placeholderText"
      persistent-placeholder :rules="autoNoEmptyRules" :loading="loading" @change="e => $emit('change', e)"
      @click:clear="$emit('clear')" @focus="setEdit" @blur="blurInput" @keypress="editKeyPress" @keydown="keyDown">
      <!--  @mouseover="setHoverBool(true)" @mouseleave="setHoverBool(false)" -->

      <!-- PREPEND SLOTS -->
      <template v-slot:prepend>
        <slot name="prepend-outer" />
      </template>

      <template v-slot:prepend-inner>
        <slot name="prepend" />
      </template>
      <!------------------->

      <!-- NO DATA TEMPLATE -->

      <template v-slot:no-data>
        <p v-if="!allowCreate" class="ma-0 px-3 py-2" style="background-color: #f2f2f2;">{{ noDataText }}</p>
      </template>
      <!--------------------------->

      <!-- SELECTION CHIPS TEMPLATE -->

      <template v-slot:selection="data">
        <slot name="selection" :data="data" :item="data.item">
          <v-chip v-if="multiple" :key="'' + refreshViewTrigger + itemValue(data.item)"
            :color="data.item.Color ? data.item.Color : (color ? color : 'primary')" :disabled="IsDisabled"
            tabindex="-1" class="ml-0"
            :style="!externaledit && !edit ? 'margin-top: 2px; margin-bottom: 2px;' : 'margin-top: 2px; margin-bottom: 2px;'"
            style="max-width: 100%;" dark small @click="data.select">
            <span class="text-truncate">{{ itemText(data.item) }}</span>
            <v-btn v-if="!IsDisabled && (clearable || (multiple && (newValue.length > 1)))" icon x-small tabindex="-1"
              style="margin-right: -10px !important;" @click="remove(data.item)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-chip>
          <div v-else :class="InputColor + '--text'">{{ itemText(data.item) }}</div>
        </slot>
      </template>
      <!--------------------------->

      <!-- PREPEND DROPDOWN ITEM TEMPLATE -->
      <template #prepend-item>
        <slot name="prepend-item">
          <!-- 
          <div v-if="!searchLoading && (searchValue && searchValue.length >= minSearchCharLimit)" class="px-2 py-1"
            style="position: sticky; top: 0; margin-right: 0px; z-index: 20; display: inline-flex; width: auto; background-color: rgba(255, 255, 255, 0.871);">
            {{ totalCount }} results
          </div>
          -->
          <template v-if="multiple && list && list.length > 2 && ((itemsPerPage / 2) >= list.length) && !searchValue">
            <v-list-item @click="toggleAll()" style="background-color: #f2f2f2 !important">
              <v-list-item-action>
                <v-checkbox :input-value="(modelValue && list && (list.length === modelValue.length))"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('servicesystem.common_objects.commonterms.all') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </slot>
      </template>
      <!----------------------------------->

      <!-- DROPDOWN ITEM TEMPLATE --------->
      <template v-if="hasSlot('item')" v-slot:item="data">
        <slot name="item" :item="data.item" :on="data.on" :attrs="data.attrs"></slot>
      </template>
      <!----------------------------------->

      <!-- APPEND DROPDOWN ITEM TEMPLATE -->
      <template #append-item>
        <slot name="append-item">
        </slot>
        <template v-if="nextPageLoading">
          <v-skeleton-loader v-for=" i in 1" :key="i" type="list-item" :style="'opacity:' + (5 - i) / 4 + ';'">
          </v-skeleton-loader>
        </template>
        <template
          v-else-if="searchValue && searchValue != '' && allowCreate && !nextPageLoading && !searchLoading && !valueTextExists(searchValue) && !valueExists(searchValue)">
          <v-divider></v-divider>
          <p class="ma-0 px-3 py-2"><v-btn x-small color="primary" @click="create">{{
              $t("servicesystem.common_objects.commonterms.create") }}: {{ searchValue }}</v-btn></p>
        </template>
        <!-- The intersect line is a trigger for loading the next page in paginated dropdowns as you scroll. -->
        <div v-intersect="endIntersect" class="intersect-point-line" :class="isProd || 'visible'"
          :style="'bottom:' + (194) + 'px;'" />

      </template>
      <!----------------------------------->

      <!-- APPEND SLOTS -->
      <template v-slot:append>
        <v-progress-circular v-if="loading" indeterminate size="20" width="2" color="primary">
        </v-progress-circular>
        <LockedWarning v-else-if="disabled" :hover="hover" />
      </template>

      <template v-slot:append-outer>
        <slot name="append-outer" />
      </template>
      <!---------------->

    </v-autocomplete>
  </v-hover>
</template>


<script>
import LockedWarning from "@/components/Inputs/Parts/LockedWarning.vue";
import InputMixin from "@/helpers/Mixins/InputMixin";
import QInputRuleMixin from "@/helpers/Mixins/QInputRuleMixin";
import { debounce } from "debounce";

export default {
  name: "QPaginatedSearchDropdown",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    label: String,
    value: [Object, String, Number, Array],
    color: String,
    singleline: Boolean,
    clearable: Boolean,
    disabled: Boolean,
    loading: Boolean,
    outlined: Boolean,
    dense: Boolean,
    externaledit: Boolean,
    emitOnlyOnBlur: Boolean,
    placeholder: [String, Object],
    rules: Array,

    listGetter: {
      type: Function,
      required: true
    },
    filter: Object,
    itemsPerPage: {
      type: Number,
      default: 100,
    },
    minSearchCharLimit: {
      type: Number,
      default: 3,
    },
    textkey: String,
    valuekey: String,
    returnobject: Boolean,
    multiple: Boolean,
    allowCreate: Boolean,
    autofocus: Boolean,
  },
  components: {
    LockedWarning,
  },
  mixins: [InputMixin, QInputRuleMixin],

  data() {
    return {
      edit: false,
      newValue: this.value,
      searchValue: "",
      list: [],
      page: 1,
      totalCount: 0,
      nextPageLoading: false,
      searchLoading: false,
      refreshViewTrigger: 1,
      cancelTokenContollerList: [],
    };
  },

  setup(props, { slots }) {
    const hasSlot = name => !!slots[name]
    return { hasSlot }
  },

  mounted() {
    this.getNextItems();
  },

  beforeDestroy() {
    this.cancelAllOpenRequests();
  },

  methods: {
    cancelAllOpenRequests() {
      //console.log('cancelling all open requests');
      for (const controller of this.cancelTokenContollerList) {
        controller.abort();
      }
      this.cancelTokenContollerList = [];
    },

    create() {
      this.$emit('create', { value: this.searchValue, onComplete: this.createComplete });
    },
    async createComplete() {
      await this.getNextItems();
      this.$refs.QPaginatedSearchDropdown.blur();
    },
    keyDown(args) {
      if (args.key.length == 1) {
        this.nextPageLoading = true;
      }
      this.editKeyDown(args);
    },
    itemText(item) {
      return this.textkey ? (typeof item === 'object' ? item[this.textkey] : item) : item;
    },

    itemValue(item) {
      return this.valuekey && !this.returnobject ? (typeof item === 'object' ? item[this.valuekey] : item) : item;
    },

    remove(item) {
      const samefunc = (x, y) => this.SameValue(x, y);
      const valuegetter = (z) => this.itemValue(z);
      if (this.multiple) {
        this.newValue = this.newValue.filter(function (x) {
          return !samefunc(x, valuegetter(item));
        });
      }
      else {
        this.newValue = null;
      }
    },

    toggleAll() {
      if (this.list.length === this.modelValue.length) {
        this.modelValue = [];
      } else {
        if (this.valuekey && !this.returnobject) {
          this.modelValue = this.list.map(x => x.Id)
        } else {
          this.modelValue = Object.assign([], this.list);
        }
      }
    },

    clearResults() {
      //console.log('clearing');
      if (this.multiple && this.newValue) {
        this.list = [...this.newValue];
      }
      else if (this.newValue) {
        if (typeof this.newValue === 'object') {
          this.list = [this.newValue];
        }
        else {
          this.list = this.list.filter(y => this.itemValue(y) == this.itemValue(this.newValue))
        }
      }
      else {
        this.list = []
      }
      this.totalCount = 0;
      this.page = 1;
    },

    debounceSearchInput: debounce(async function (keyword) {
      if (this.newValue && keyword == this.itemText(this.newValue)) {
        //console.log('killed debounce');
        return;
      }
      if (!this.minSearchCharLimit || (keyword && keyword.length >= this.minSearchCharLimit)) {
        this.cancelAllOpenRequests();
        this.searchLoading = true;
        await this.getNextItems();
        this.searchLoading = false;
      }
    }, 700),

    endIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && !this.nextPageLoading) {
        this.page++;
        this.getNextItems();
      }
    },

    async getNextItems() {
      const controller = new AbortController();
      const cancelationToken = controller.signal;
      this.cancelTokenContollerList.push(controller);
      var prependItems = this.missingValues;
      this.nextPageLoading = true;
      var filterDto = Object.assign({}, this.filter)
      this.page = 1;
      filterDto.SearchWord = this.searchValue;
      filterDto.ItemsPerPage = this.itemsPerPage;
      filterDto.Page = this.page;
      filterDto.PrependItemIdList = prependItems;

      var resultWrapper = await this.listGetter(filterDto, cancelationToken);
      var pageItems;
      if (resultWrapper.Content) {
        pageItems = resultWrapper.Content;
        this.totalCount = resultWrapper.TotalCount;
      }
      else {
        this.totalCount = 0;
        pageItems = resultWrapper;
      }

      if (this.searchValue) this.clearResults();
      this.list = [...this.list, ...pageItems];

      this.nextPageLoading = false;
    },

    valueExists(val) {
      return this.list.some(y => this.itemValue(y) == this.itemValue(val))
    },
    valueTextExists(val) {
      if (this.list.length == 0 || !val) return false;
      return this.list.some(y => this.itemText(y) == this.itemText(val))
    },
  },

  computed: {
    noDataText() {
      if (this.minSearchCharLimit && (!this.searchValue || (this.searchValue.length < this.minSearchCharLimit))) {
        return this.$t('servicesystem.common_objects.commonterms.type_three_chars_to search')
      }
      else if (this.searchLoading) {
        return this.$t('servicesystem.common_objects.commonterms.searching')
      }
      else {
        return this.$t('servicesystem.common_objects.commonterms.no_results')
      }
    },

    isProd() {
      return (process.env.NODE_ENV === 'production');
    },

    missingValues() {
      var list = [];
      if (this.multiple && this.value && this.value.length) {
        list = this.value.filter((x) => (!this.valueExists(x)))
      }
      else if (this.value && !this.valueExists(this.value)) {
        list.push(this.value)
      }
      if (list.length) {
        if (!this.returnobject) {
          list = list.map(z => this.itemValue(z));
        }
        else if (this.valuekey) {
          list = list.map(z => z[this.valuekey]);
        }
      }
      list = list.filter(x => x != null);

      return list;
    },

    autoNoEmptyRules() {
      var r = Object.assign([], this.rules);
      if (!this.clearable && !r?.some(x => x.name == 'requiredRule')) {
        r = [...r, this.requiredRule];
      }
      //console.log(r);
      return r;
    },

    IsRequired() { //ovverides default from mixin to handle the autoNoEmptyRules
      return (this.autoNoEmptyRules && this.autoNoEmptyRules.some(x => x.name == 'requiredRule'));
    },
  },

  watch: {
    filter: {
      handler() {
        this.clearResults();
        this.debounceSearchInput(this.searchValue);
      },
      deep: true
    },
    value: function (newVal, oldVal) {
      if (newVal != oldVal) {
        this.refreshViewTrigger++;
      }
    },

  },
};
</script>


<style lang="scss">
/* classes bellow fix a vuetify bug that breaks the persistant-placeholder when the input is unfocused */
.v-autocomplete:not(.v-input--is-focused).v-select--chips>>>input {
  max-height: inherit;
}

.v-autocomplete:not(.v-input--is-focused).v-select--chips input {
  max-height: inherit;
}

.intersect-point-line {
  content: '';
  width: 100%;
  height: 1px;
  position: absolute;

}

.intersect-point-line.visible {
  background-color: orange;
}
</style>